import { useDisclosure } from '@mantine/hooks';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
	createFacetedRowModel,
	createFacetedUniqueValues,
	createFilteredRowModel,
	createPaginatedRowModel,
	createSortedRowModel,
	useTable,
} from '@tanstack/react-table';
import type { AxiosProgressEvent } from 'axios';

import { AddIcon, DownloadIcon, Upload } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useCurrentLocation } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { ToolbarButton } from '@apple/ui/data-table/controls/Toolbar/toolbar-button';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { TableLayout } from '@apple/ui/layouts/TableLayout';
import { Link } from '@apple/ui/link/Link';
import { ExcelUploadDialog } from '@apple/utils/files/components/ExcelUploadDialog';
import { useFileDownload } from '@apple/utils/files/hooks/useFileDownload';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from '@apple/utils/pagination/models';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import {
	deleteAssociations,
	saveAssociations,
	uploadVintageAssociations,
} from '@apple/features/vintage/api';
import type { VintageBulkUploadOperation } from '@apple/features/vintage/models';
import { vintageQueries, vintageQueryKeys } from '@apple/features/vintage/queries';

const features = createDataTableFeatures({
	filteredRowModel: createFilteredRowModel(),
	sortedRowModel: createSortedRowModel(),
	paginatedRowModel: createPaginatedRowModel(),
	facetedRowModel: createFacetedRowModel(),
	facetedUniqueValues: createFacetedUniqueValues(),
});

export const Route = createFileRoute('/_authed/_admin/manage/vintages')({
	beforeLoad: args => {
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.CustomerManagement',
		});
	},
	loader: ({ context: { queryClient } }) => {
		void queryClient.prefetchQuery(vintageQueries.listing);
	},
	component: VintageManagementRoute,
});

function VintageManagementRoute() {
	const { t } = useTranslation('vintage');
	const [uploadOpen, uploadActions] = useDisclosure(false);
	const navigate = Route.useNavigate();
	const location = useCurrentLocation();
	const queryClient = useQueryClient();
	const vintagesQuery = useQuery(vintageQueries.listing);
	const excelTemplate = useFileDownload({
		method: 'get',
		url: '/api/management/vintages/bulk/excel/generate',
		fallbackFilename: 'vintages.xlsx',
		errorTitle: t('common:error.title'),
		errorMessage: t('common:error.fileDownload'),
	});

	const handleUpload = async (
		file: File,
		onUploadProgress?: (event: AxiosProgressEvent) => void,
	) => {
		const results = await uploadVintageAssociations(file, onUploadProgress);
		return results;
	};

	const table = useTable({
		features,
		data: vintagesQuery.data,
		enableFilters: true,
		enableColumnFilters: true,
		enableSorting: true,
		columnResizeMode: 'onChange',
		initialState: {
			pagination: { pageSize: DEFAULT_PAGE_SIZE, pageIndex: DEFAULT_PAGE_INDEX },
		},
		columns: [
			{
				accessorKey: 'name',
				header: t('columns.name'),
				filter: { group: t('stores.filterGroup') },
				size: 250,
				cell: ({ row }) => (
					<Link
						to='/manage/vintages/$vintageId'
						params={{ vintageId: row.original.vintageId.toString() }}
						title={row.original.name}
						children={row.original.name}
					/>
				),
			},
			{
				accessorKey: 'description',
				header: t('columns.description'),
				size: 300,
				cell: ({ row }) => row.original.description ?? '',
			},
			{
				accessorKey: 'storesAssigned',
				header: t('columns.storesAssigned'),
				enableColumnFilter: false,
				size: 150,
			},
			{
				accessorKey: 'productsTagged',
				header: t('columns.productsTagged'),
				enableColumnFilter: false,
				size: 150,
			},
		],
	});

	return (
		<TableLayout
			title={t('title')}
			table={table}
			toolbarButtons={[
				<ToolbarButton
					key='download-budget-template'
					tooltip={t('buttons.downloadTooltip')}
					icon={DownloadIcon}
					loading={excelTemplate.downloading}
					onClick={() => void excelTemplate.download()}
				/>,
				<ToolbarButton
					key='upload'
					tooltip={t('buttons.uploadTooltip')}
					icon={Upload}
					disabled={uploadOpen}
					onClick={uploadActions.open}
					data-testid='upload-bulk-template'
				/>,
				<ToolbarButton
					key='add'
					tooltip={t('buttons.addTooltip')}
					icon={AddIcon}
					onClick={() =>
						void navigate({ to: '/manage/vintages/add', state: { from: location } })
					}
					data-testid='add-vintage'
				/>,
			]}
		>
			<DataTable table={table} loading={vintagesQuery.isFetching} />

			<ExcelUploadDialog<VintageBulkUploadOperation, 'id'>
				keyProperty={'id'}
				keyLabel={t('fields.id')}
				opened={uploadOpen}
				close={uploadActions.close}
				onUpload={handleUpload}
				onSave={associations => saveAssociations(associations)}
				onDelete={itemIds => deleteAssociations(itemIds)}
				getWarning={item =>
					item.type === 'Store' &&
					item.currentVintageName &&
					item.currentVintageName !== item.vintageName
						? t('bulk.storeMoveWarning', {
								storeId: item.storeId ?? item.id,
								currentVintageName: item.currentVintageName,
								vintageName: item.vintageName,
							})
						: undefined
				}
				onConfirmed={async () => {
					queryClient.removeQueries({
						queryKey: vintageQueryKeys.all,
						type: 'inactive',
					});
					await queryClient.invalidateQueries({ queryKey: vintageQueryKeys.listing });
				}}
			/>
		</TableLayout>
	);
}
